"use client";

import { useMemo, useRef, useState } from "react";
import { ArrowRight, Calculator, CheckCircle2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import FrequencySelect from "@/components/frequency-select";
import SectionHeading from "@/components/section-heading";
import TrackedWebAppLink from "@/components/tracked-web-app-link";
import { useAnalytics } from "@/components/analytics/use-analytics";
import { usePricingCatalog } from "@/hooks/usePricingCatalog";
import { formatCurrency } from "@/lib/format-currency";

// Checks per URL per month, using a 30-day month so "once a day" reads as 30.
const FREQUENCY_OPTIONS = [
  { key: "hourly", checksPerMonth: 24 * 30 },
  { key: "every6h", checksPerMonth: 4 * 30 },
  { key: "every12h", checksPerMonth: 2 * 30 },
  { key: "daily", checksPerMonth: 30 },
  { key: "weekly", checksPerMonth: 30 / 7 },
] as const;

const PRICING_RULE_KEYS = ["perPage", "frequency", "topUp"] as const;

/** See the `pagesPerCheck` state below for why this is 2 and not 1.62. */
const DEFAULT_PAGES_PER_CHECK = 2;

/**
 * Monthly checks, PAGES and cost for a modelled operation.
 *
 * A check costs one credit per page it reads (#1767): the source page, plus any
 * item pages it follows. `pagesPerCheck` is what the visitor is modelling, so
 * the cost tracks pages rather than checks — the two only coincide for a source
 * whose items have no pages of their own, which is the majority but not all.
 */
export function estimateMonthlyUsage({
  urls,
  checksPerUrl,
  pagesPerCheck = 1,
  perCheckMajor,
}: {
  urls: number;
  checksPerUrl: number;
  pagesPerCheck?: number;
  perCheckMajor?: number;
}) {
  const safeUrls = Number.isFinite(urls) && urls > 0 ? urls : 0;
  const safePages =
    Number.isFinite(pagesPerCheck) && pagesPerCheck > 0 ? pagesPerCheck : 1;
  const checks = Math.round(safeUrls * checksPerUrl);
  const pages = Math.round(checks * safePages);

  return {
    checks,
    pages,
    cost: perCheckMajor === undefined ? null : pages * perCheckMajor,
  };
}

type FrequencyKey = (typeof FREQUENCY_OPTIONS)[number]["key"];

const FREQUENCY_KEYS = new Set<string>(
  FREQUENCY_OPTIONS.map((option) => option.key),
);

function isFrequencyKey(value: string): value is FrequencyKey {
  return FREQUENCY_KEYS.has(value);
}

const PricingCalculator = () => {
  const locale = useLocale();
  const t = useTranslations("pricingCalculator");
  const { trackEvent } = useAnalytics();
  const hasTrackedRef = useRef(false);
  // Single source of truth for what the page shows: the catalog is read by the
  // checkout action AND every displayed price, so the rendered number cannot
  // drift from what Stripe charges (issue #1012). `perCheckMajor` is the
  // hook's single per-check derivation reused for the cost math; `pricingValues`
  // is the no-leak placeholder bundle every `t(key, …)` call passes to the
  // calculator's own copy.
  const {
    perCheckMajor,
    currency,
    pricingValues,
    maxItemPagesPerCheck,
    loading: pricingLoading,
  } = usePricingCatalog(locale);
  // What a check of the source this field models — one whose ITEMS have their
  // own pages — can read: the source page plus its fan-out. Not the per-check
  // ceiling, which since #2679 also covers the continuation pages of a
  // paginated list, and those are not item pages. Falls back to 1 while the
  // catalog loads, which pins the field to the single-page case rather than
  // inventing a bound.
  const maxPagesPerCheck = maxItemPagesPerCheck ?? 1;
  const catalogReady = !pricingLoading;

  const [urls, setUrls] = useState(5);
  const [frequency, setFrequency] = useState<FrequencyKey>("daily");
  // Pages a check reads. Defaults to 2, not to the 1.62 measured in production:
  // that figure counts `content_changes` rows, and an unchanged page reuses its
  // previous row instead of writing a new one, so 1.62 is a FLOOR. Rounding up
  // keeps the projection from under-quoting.
  const [pagesPerCheck, setPagesPerCheck] = useState(DEFAULT_PAGES_PER_CHECK);
  const trackFirstInteraction = () => {
    if (hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;
    try {
      trackEvent("pricing_calculator_used");
    } catch {
      // Analytics is best-effort and must never break the widget.
    }
  };

  const {
    checks: checksPerMonth,
    pages: pagesPerMonth,
    cost: briefpanelCost,
  } = useMemo(() => {
    const perUrl =
      FREQUENCY_OPTIONS.find((option) => option.key === frequency)
        ?.checksPerMonth ?? 30;
    return estimateMonthlyUsage({
      urls,
      checksPerUrl: perUrl,
      pagesPerCheck,
      perCheckMajor,
    });
  }, [urls, frequency, pagesPerCheck, perCheckMajor]);
  const localizedChecks = checksPerMonth.toLocaleString(locale);
  const localizedPages = pagesPerMonth.toLocaleString(locale);
  const formattedCost =
    catalogReady && currency && briefpanelCost !== null
      ? formatCurrency(briefpanelCost, locale, currency)
      : null;

  return (
    <section id="billing" className="bg-background py-20 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <SectionHeading
          eyebrow={t("eyebrow")}
          title={t("title")}
          description={
            catalogReady
              ? t("description", pricingValues)
              : t("descriptionLoading")
          }
          eyebrowClassName="text-muted-foreground"
        />

        <div className="mx-auto mb-6 max-w-5xl rounded-3xl border border-border bg-muted/50 p-6 shadow-sm md:p-8">
          <div className="grid gap-5 md:grid-cols-3">
            {PRICING_RULE_KEYS.map((key) => (
              <div key={key} className="flex items-start gap-3">
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <p className="text-sm leading-relaxed text-foreground">
                  {key === "topUp" && !catalogReady
                    ? t("rules.topUpLoading")
                    : t(`rules.${key}`, pricingValues)}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-6 border-t border-border pt-5 text-sm leading-relaxed text-muted-foreground">
            {catalogReady
              ? t("offer", pricingValues)
              : t("offerLoading")}
          </p>
        </div>

        <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8">
          {/* Inputs */}
          <div className="flex flex-col gap-6 rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted text-foreground">
                <Calculator className="h-5 w-5" />
              </span>
              <p className="text-lg font-semibold text-foreground">
                {t("inputsTitle")}
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="calc-urls"
                className="text-sm font-medium text-foreground"
              >
                {t("urlsLabel")}
              </label>
              <input
                id="calc-urls"
                type="number"
                min={1}
                max={100000}
                inputMode="numeric"
                value={Number.isFinite(urls) ? urls : ""}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  setUrls(Number.isNaN(next) ? 0 : Math.min(next, 100000));
                  trackFirstInteraction();
                }}
                className="w-full rounded-xl border border-input bg-background px-4 py-3 text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>

            <div className="space-y-2">
              <label
                id="calc-frequency-label"
                htmlFor="calc-frequency"
                className="text-sm font-medium text-foreground"
              >
                {t("frequencyLabel")}
              </label>
              {/* Custom dropdown (not a native <select>): the native open menu
                  is drawn by the OS and can't be themed — it renders as a light
                  system menu over the dark UI. FrequencySelect renders the list
                  as themed DOM instead. */}
              <FrequencySelect
                id="calc-frequency"
                labelId="calc-frequency-label"
                value={frequency}
                options={FREQUENCY_OPTIONS.map((option) => ({
                  value: option.key,
                  label: t(`frequency.${option.key}`),
                }))}
                onChange={(next) => {
                  if (isFrequencyKey(next)) {
                    setFrequency(next);
                  }
                }}
                onInteract={trackFirstInteraction}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="calc-pages"
                className="text-sm font-medium text-foreground"
              >
                {t("pagesLabel")}
              </label>
              <input
                id="calc-pages"
                type="number"
                min={1}
                max={maxPagesPerCheck}
                inputMode="numeric"
                value={Number.isFinite(pagesPerCheck) ? pagesPerCheck : ""}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  setPagesPerCheck(
                    Number.isNaN(next)
                      ? 1
                      : Math.min(Math.max(next, 1), maxPagesPerCheck),
                  );
                  trackFirstInteraction();
                }}
                className="w-full rounded-xl border border-input bg-background px-4 py-3 text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t("pagesHint", {
                  maxPages: maxPagesPerCheck,
                  maxContinuationPages: pricingValues.maxContinuationPages,
                })}
              </p>
            </div>

            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("estimateNote")}
            </p>
          </div>

          {/* Results */}
          <div className="flex flex-col gap-5 rounded-3xl border border-border bg-muted p-6 md:p-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {t("results.eyebrow")}
              </p>
              <h3 className="mt-2 text-xl font-semibold text-foreground">
                {t("results.title")}
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card px-4 py-3">
                <span className="text-sm font-medium text-muted-foreground">
                  {t("results.checksTitle")}
                </span>
                <span className="text-xl font-bold text-foreground">
                  {localizedChecks}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card px-4 py-3">
                <span className="text-sm font-medium text-muted-foreground">
                  {t("results.pagesTitle")}
                </span>
                <span className="text-xl font-bold text-foreground">
                  {localizedPages}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card px-4 py-3">
                <span className="text-sm font-semibold text-foreground">
                  {t("results.briefpanelTitle")}
                </span>
                <span className="whitespace-nowrap text-xl font-bold text-foreground">
                  {formattedCost ?? "—"}
                  <span className="text-sm font-medium text-muted-foreground">
                    {t("results.perMonth")}
                  </span>
                </span>
              </div>
            </div>

            <p className="sr-only" aria-live="polite" aria-atomic="true">
              {formattedCost
                ? t("results.a11ySummary", {
                    checks: localizedChecks,
                    pages: localizedPages,
                    cost: formattedCost,
                  })
                : t("results.loading")}
            </p>

            <div
              className="rounded-2xl border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground"
              aria-live="polite"
            >
              {catalogReady && currency && briefpanelCost !== null
                ? t("results.guidance")
                : t("results.loading")}
            </div>

            <TrackedWebAppLink
              href="https://app.briefpanel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-md transition hover:bg-primary/90"
              location="pricing_calculator"
              label={t("cta")}
            >
              {t("cta")}
              <ArrowRight className="h-4 w-4" />
            </TrackedWebAppLink>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingCalculator;
